/**
 * Progresso da sessão de estudo. Só formata os números que o hook da sessão
 * já calculou (`posicao`, `faltam`, `contagens`). Não reconta a fila.
 */
import type { DueCounts } from "../../../sdk/@types/Revisao";

export interface ProgressoSessaoProps {
  /** 1-based position while studying. Pass through. Do not derive from an index. */
  posicao: number;
  total: number;
  /** Cards still after the current one. Pass through. Do not compute total - posicao here. */
  faltam: number;
  /** From the same GET /review/due that built the queue. Display as given. */
  contagens: DueCounts;
  /** "Marcação N de M" from `rotuloMarcacaoDoCartao`. Omit when it returns null. */
  marcacao?: string | null;
}

const CHIP_CLASS =
  "rounded-full bg-surface-muted px-2.5 py-0.5 text-xs font-medium text-ink-secondary";

function frasePosicao(posicao: number, total: number): string {
  return `${posicao} de ${total}`;
}

function fraseFaltam(faltam: number): string {
  if (faltam === 1) return "Falta 1";
  return `Faltam ${faltam}`;
}

export function ProgressoSessao({
  posicao,
  total,
  faltam,
  contagens,
  marcacao,
}: ProgressoSessaoProps): JSX.Element {
  const posicaoTexto = frasePosicao(posicao, total);
  const faltamTexto = fraseFaltam(faltam);
  const pct = total > 0 ? (posicao / total) * 100 : 0;
  const marcacaoVisivel = typeof marcacao === "string" && marcacao !== "";

  return (
    <div className="w-full" data-sessao-progresso="">
      <div className="flex min-w-0 items-center justify-between gap-3 text-sm font-medium tabular-nums text-ink">
        {marcacaoVisivel ? (
          <span className="flex min-w-0 flex-wrap">
            <span data-marcacao="">{marcacao}</span>
            {" · "}
            <span data-sessao-posicao="">{posicaoTexto}</span>
          </span>
        ) : (
          <span className="min-w-0" data-sessao-posicao="">
            {posicaoTexto}
          </span>
        )}
        <span className="min-w-0">{faltamTexto}</span>
      </div>
      <div
        className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={posicao}
        aria-label={
          marcacaoVisivel
            ? `${marcacao}. ${posicaoTexto}. ${faltamTexto}`
            : `${posicaoTexto}. ${faltamTexto}`
        }
      >
        <div className="h-full rounded-full bg-rate-easy" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <span className={CHIP_CLASS}>Novos {contagens.new}</span>
        <span className={CHIP_CLASS}>Aprendendo {contagens.learning}</span>
        <span className={CHIP_CLASS}>Revisar {contagens.review}</span>
      </div>
    </div>
  );
}
